import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  Search,
  Table as TableIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useEmployees } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { PDPS_BASE } from '../api';
import type { PDP } from '../api';
import { PDP_STATUSES, pdpStatusLabel, pdpStatusVariant } from '../constants';
import { usePdps } from '../queries';
import { usePdpsUi, type PdpsViewMode } from '../store/viewPrefs';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type PdpOrderingColumn = 'title' | 'period' | 'status';

const VIEW_MODES: readonly ViewModeOption<PdpsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

export function PdpsPage() {
  const navigate = useNavigate();
  const view = usePdpsUi((s) => s.view);
  const setView = usePdpsUi((s) => s.setView);
  const ordering = usePdpsUi((s) => s.ordering);
  const setOrdering = usePdpsUi((s) => s.setOrdering);

  // Server-driven filters live in page-local state — pagination + search hit the API.
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = usePdps({
    page,
    search: search || undefined,
    status: status === 'all' ? undefined : (status as 'active' | 'completed'),
    ordering,
  });

  // PDP rows carry only an employee id; resolve display names from the
  // employee list.
  const { data: employeesData } = useEmployees({});
  const employeeLabels = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employeesData?.results ?? []) map.set(e.id, employeeName(e));
    return map;
  }, [employeesData]);
  const labelFor = (pdp: PDP) =>
    employeeLabels.get(pdp.employee) ?? `Employee #${pdp.employee}`;

  const pdps = data?.results ?? [];
  const open = (pdp: PDP) => navigate(`/hrm/pdp/${pdp.public_id}`);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Personal Development Plans</h1>
          <p className="text-muted-foreground text-sm">Per-employee goals + progress.</p>
        </div>
        <Button onClick={() => navigate('/hrm/pdp/new')}>
          <Plus className="size-4" /> New PDP
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by employee, title or period…"
              className="pl-8"
              aria-label="Search PDPs"
            />
          </div>
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {PDP_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `pdps.csv` without leaving the SPA.
              void exportCsv(PDPS_BASE, {
                search: search || undefined,
                status: status === 'all' ? undefined : status,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export PDPs as CSV"
            title="Export PDPs as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load PDPs.'}
        </p>
      ) : isPending ? (
        <PdpsSkeleton />
      ) : pdps.length === 0 ? (
        <PdpsTable
          pdps={[]}
          labelFor={labelFor}
          onOpen={open}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <PdpsTable
          pdps={pdps}
          labelFor={labelFor}
          onOpen={open}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <PdpsList pdps={pdps} labelFor={labelFor} onOpen={open} />
      ) : view === 'cards' ? (
        <PdpsCards pdps={pdps} labelFor={labelFor} onOpen={open} />
      ) : (
        <PdpsGroupedByStatus pdps={pdps} labelFor={labelFor} onOpen={open} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'plan' : 'plans'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface PdpsViewProps {
  pdps: PDP[];
  labelFor: (pdp: PDP) => string;
  onOpen: (pdp: PDP) => void;
}

interface PdpsTableProps extends PdpsViewProps {
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function progress(pdp: PDP): number {
  const doneCount = pdp.goals.filter((g) => g.status === 'done').length;
  return pdp.goals.length ? Math.round((doneCount / pdp.goals.length) * 100) : 0;
}

function PdpsTable({ pdps, labelFor, onOpen, ordering, onOrderingChange }: PdpsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Employee</TableHead>
            <SortableTableHead<PdpOrderingColumn>
              column="title"
              value={ordering}
              onChange={onOrderingChange}
            >
              Title
            </SortableTableHead>
            <SortableTableHead<PdpOrderingColumn>
              column="period"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Period
            </SortableTableHead>
            <SortableTableHead<PdpOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pdps.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-10 text-center">
                No development plans found.
              </TableCell>
            </TableRow>
          ) : (
            pdps.map((pdp) => (
              <TableRow key={pdp.id} className="cursor-pointer" onClick={() => onOpen(pdp)}>
                <TableCell className="font-medium">{labelFor(pdp)}</TableCell>
                <TableCell>{pdp.title}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {pdp.period || '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={pdpStatusVariant(pdp.status)}>
                    {pdpStatusLabel(pdp.status)}
                  </Badge>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function PdpsCards({ pdps, labelFor, onOpen }: PdpsViewProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {pdps.map((pdp) => (
        <Card
          key={pdp.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(pdp)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(pdp);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
            <div className="min-w-0">
              <CardTitle className="text-base">{pdp.title}</CardTitle>
              <p className="text-muted-foreground text-sm">
                {labelFor(pdp)} · {pdp.period || 'No period'} · {progress(pdp)}% done
              </p>
            </div>
            <Badge variant={pdpStatusVariant(pdp.status)}>{pdpStatusLabel(pdp.status)}</Badge>
          </CardHeader>
          {pdp.summary ? (
            <CardContent>
              <p className="text-muted-foreground line-clamp-2 text-sm">{pdp.summary}</p>
            </CardContent>
          ) : null}
        </Card>
      ))}
    </div>
  );
}

function PdpsList({ pdps, labelFor, onOpen }: PdpsViewProps) {
  return (
    <ul className="divide-y rounded-md border">
      {pdps.map((pdp) => (
        <li key={pdp.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(pdp)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm"
          >
            <span className="min-w-0 flex-1 truncate font-semibold">{pdp.title}</span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {labelFor(pdp)}
            </span>
            <span className="text-muted-foreground hidden text-xs md:inline">
              {pdp.period || '—'}
            </span>
            <span className="text-muted-foreground text-xs">{progress(pdp)}% done</span>
            <Badge variant={pdpStatusVariant(pdp.status)} className="shrink-0">
              {pdpStatusLabel(pdp.status)}
            </Badge>
          </button>
        </li>
      ))}
    </ul>
  );
}

function PdpsGroupedByStatus({ pdps, labelFor, onOpen }: PdpsViewProps) {
  // Bucket by status, preserving the declared status order; skip empty groups.
  const groups = useMemo(() => {
    return PDP_STATUSES.map((s) => ({
      key: s.value,
      label: s.label,
      bucket: pdps.filter((p) => p.status === s.value),
    }));
  }, [pdps]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`pdps-status-${key}`}>
            <h2
              id={`pdps-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <PdpsCards pdps={bucket} labelFor={labelFor} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function PdpsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
